import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  OnDestroy,
  untracked,
  viewChild,
  inject,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { FileDocumentTypesEffect } from './shared/state/global-state/file-document-types.effect';
import { UrlParamsState } from './shared/state/url-params/url-params.state';
import { TaskDetailsHashmapEffect } from './shared/state/task-details/task-details-hashmap.effect';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { SummaryPanelComponent } from './shared/components/summary-panel/summary-panel.component';
import { ActiveTaskDetailsProviderService } from '../tasks-worker/shared/state/combined-state/active-task-details.provider.service';
import { TaskTypes } from 'src/app/shared/models/tasks.model';
import { ActiveAccountProviderService } from '../tasks-worker/renderers/create-package-task-type/shared/state/combined-state/active-account-provider.service';
import { NgTemplateOutlet } from '@angular/common';
import { TaskItemStatus } from '@r1-development/resolution-mechanicalturk-api-client';

@Component({
  selector: 'ri-resolution-ui-routes-with-sidebar',
  imports: [
    RouterOutlet,
    NgTemplateOutlet,
    SummaryPanelComponent,
    SidebarComponent,
  ],
  templateUrl: './routes-with-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutesWithSidebarComponent implements OnDestroy {
  private readonly urlParamsState = inject(UrlParamsState);
  private readonly taskDetailsHashmapEffect = inject(TaskDetailsHashmapEffect);
  private readonly activeTaskDetailsProviderService = inject(
    ActiveTaskDetailsProviderService
  );
  private readonly activeAccountProviderService = inject(
    ActiveAccountProviderService
  );
  private readonly fileDocumentTypesEffect = inject(FileDocumentTypesEffect);

  public readonly sidebarRef = viewChild<SidebarComponent>('sidebarRef');

  public readonly defaultExpandSummaryPanel = computed<boolean>(() => {
    return (
      this.activeTaskDetailsProviderService.activeTaskDetails()?.taskType !==
      TaskTypes.CreatePackageTaskItem
    );
  });

  public readonly shouldShowSummaryPanel = computed<boolean>(() => {
    const activeTask =
      this.activeTaskDetailsProviderService.activeTaskDetails();
    if (!activeTask?.taskType) {
      return false;
    }

    return (
      [TaskTypes.CallPayerTaskItem, TaskTypes.CreatePackageTaskItem].includes(
        activeTask.taskType
      ) &&
      !(
        activeTask.taskType === TaskTypes.CreatePackageTaskItem &&
        activeTask.status === TaskItemStatus.Completed
      )
    );
  });

  public readonly activeAccount =
    this.activeAccountProviderService.activeAccount;

  public readonly rightPanelSummaryData = computed(() => {
    return {
      issueCategory:
        this.activeTaskDetailsProviderService.activeTaskDetails()?.keyDetails
          ?.issueCategory || 'No data available',
      llmNoteSummary:
        this.activeAccount()?.llmNoteSummary || 'No data available',
      opportunitySummary:
        this.activeAccount()?.opportunitySummary || 'No data available',
      facilityAddresses: this.activeAccount()?.facility?.addresses || [],
    };
  });

  public readonly showOverlay = computed<boolean>(() => {
    const sidebar = this.sidebarRef();
    return sidebar ? sidebar.searchInteractionDirty() : false;
  });

  constructor() {
    this.onActiveTaskChange();
    this.fileDocumentTypesEffect.loadFileDocumentTypesToState();
  }

  ngOnDestroy(): void {
    this.urlParamsState.setActiveTaskId(null);
  }

  reloadSidebar(): void {
    const sidebarRef = this.sidebarRef();
    if (!sidebarRef) {
      console.warn(
        'Method is called too early, there is no sidebarRef available yet'
      );
      return;
    }

    sidebarRef!.reloadData();
  }

  private onActiveTaskChange(): void {
    effect(() => {
      const activeTaskId = this.urlParamsState.activeTaskId();
      untracked(() => {
        if (activeTaskId) {
          // Reset active entities
          this.taskDetailsHashmapEffect.reloadState(activeTaskId, {
            shouldReloadOnAging: true,
            numberOfSecondsToTreatAsStaleState: 10 * 60,
          });
        }
      });
    });
  }
}
